import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Print } from "@mui/icons-material";
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import { CONTRACT_TERMS_ENDPOINT } from '../../../../consts';
import { useHasPermission } from '../../../../contexts/auth';
import { formatNumber } from '../../../../utils';
import { getContractTermEquipmentLabel } from '../../../../utils/settlementContractTerms';
import EquipmentReferenceList from '../../../../components/ui/entityReference/equipmentReferenceList.component';
import { ContractTermListPrice } from '../../../../types/contracts';

export const ContractTerms = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canViewContractTerms = useHasPermission("settlement.view_contractterm");
  const canViewEquipment = useHasPermission("settlement.view_equipment");

  const columns: GridColDef[] = useMemo(() => [
    {
      field: "id",
      headerName: "ID",
    },
    {
      field: "equipments",
      headerName: t("Equipment"),
      valueGetter: (_value, row) => getContractTermEquipmentLabel(row),
      renderCell: (params) => (
        <EquipmentReferenceList
          equipments={params.row.equipments || []}
          canPreview={canViewEquipment}
        />
      ),
    },
    {
      field: "kind",
      headerName: t("Kind"),
      valueGetter: (value, row) => row.kind_display,
    },
    {
      field: "active",
      headerName: t("Active"),
      type: "boolean",
      valueGetter: (_value, row) => row.active !== false,
      renderCell: (params) => {
        const isActive = params.row.active !== false;
        return (
          <Chip
            label={isActive ? t("Yes") : t("No")}
            size="small"
            color={isActive ? "success" : "default"}
            variant={isActive ? "filled" : "outlined"}
          />
        );
      },
    },
    {
      field: "settlement_cycle",
      headerName: t("Settlement Cycle"),
      valueGetter: (value, row) => row.settlement_cycle_display,
    },
    {
      field: "settlement_kind_display",
      headerName: t("Settlement kind"),
      valueGetter: (value, row) => row.settlement_kind_display || "-",
    },
    {
      field: "next_settlement_date",
      headerName: t("Next Settlement Date"),
    },
    {
      field: "rent_price",
      headerName: t("Rent Price"),
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "prices_info",
      headerName: t("Prices"),
      sortable: false,
      flex: 1,
      minWidth: 150,
      valueGetter: (_value, row) => {
        const prices: ContractTermListPrice[] = row.prices_info || [];
        return prices.map((p) => `${p.kind_display || "-"}: ${p.price} zł`).join(", ");
      },
      renderCell: (params) => {
        const prices: ContractTermListPrice[] = params.row.prices_info || [];
        if (prices.length === 0) {
          return <Typography variant="body2">-</Typography>;
        }
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, py: 0.5 }}>
            {prices.map((price, index) => (
              <Typography key={index} variant="body2">
                {price.kind_display || "-"}: {formatNumber(price.price)} zł
              </Typography>
            ))}
          </Box>
        );
      },
    },
  ], [t, canViewEquipment]);

  const handleRowClick = (contractTermId: string) => {
    navigate(`/contracts/${id}/equipment/${contractTermId}`);
  };

  if (!canViewContractTerms) {
    return null;
  }

  const contractTermsUrl = id ? `${CONTRACT_TERMS_ENDPOINT}?contract=${id}` : CONTRACT_TERMS_ENDPOINT;

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <CustomDataGrid
        icon={<Print />}
        title={t('Equipments')}
        url={contractTermsUrl}
        gridSettingsKey="CONTRACT_TERMS"
        columns={columns}
        onRowClick={handleRowClick}
        refreshEventName="contractTermUpdated"
      />
    </Box>
  );
};
